import { Box, Modal, Text } from '@fozito/common-ui';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useSocketContext } from '../../hooks/useSocket';
import { Form } from 'antd';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { IResponseData } from 'services/response';
import cashierApis from 'modules/POS/services/apis';
import { InputWithKeyboardStyled, useDollarAmountInputRef } from '../../components/DollarAmount/Input';
import styled from 'styled-components';
import DollarAmountKeyboard from '../../components/DollarAmount/Keyboard';
import { formatCurrency } from 'utils/formatCurrency';
import shopService from 'modules/shop/services';
import { ICashierLoyaltyPointPayment } from 'modules/POS/services/types/payment';
import { ICustomerPaymentDetail, TypePaymentActors, TypePaymentPassData } from '../../socket.interface';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import { debounce } from 'lodash';
import { PAYMENT_TYPE } from '../../cashier.constants';
import ModalActions from '../../components/ModalActions';
import { setPageLoading } from 'widgets/PageLoading';
import { cashierBtnMultiPayV3 } from '../MultiPayment';
import { cashierCtrl } from '../../cashier.controller';
import useListenRequestSync from '../../hooks/useListenRequestSync';
export type ICashierLoyaltyPaymentMethod = (billId: string, payment: ICashierLoyaltyPointPayment) => Promise<boolean>;
type Props = {
  customerPoint?: number;
  onPayment: ICashierLoyaltyPaymentMethod;
};
type Ref = {
  open: (billId: string) => Promise<void>;
};
export const useModalLoyaltyRef = () => useRef<Ref>(null);
const ModalCashierLoyaltyPayment = forwardRef<Ref, Props>(({ customerPoint = 0, onPayment }, ref) => {
  const [form] = Form.useForm();
  const dollarRef = useDollarAmountInputRef();
  const socketContext = useSocketContext();
  const [billId, setOpen] = useState<string>('');
  const onClose = () => {
    socketContext.backToCheckIn();
    setOpen('');
  };
  const loyaltySetting = shopService.selectors.loyaltyRedeem();

  const amount = Form.useWatch('amount', form);
  const _amount = amount || 0;

  const redeemPoint = Math.round(loyaltySetting ? (+_amount) * (loyaltySetting?.oneDollarToPoint || 0) : 0);
  const balancePoint = +(customerPoint || '') - redeemPoint;

  const exchangeDollar = (point: number) => {
    if (!loyaltySetting) return 0;
    const val = +(point) / loyaltySetting?.oneDollarToPoint;
    return val;
  };

  const fetchDetail = async (billId: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(billId);
      const detail = res.data.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(res.data.data);
        const availablePointEx = exchangeDollar(detail.customerPoint || 0);
        const amountVal = availablePointEx - detail?.total < 0 ? availablePointEx : detail?.total;
        form.setFieldValue('detail', res.data.data);
        form.setFieldValue('amount', amountVal);

        socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
          billId,
          actor: TypePaymentActors.CASHIER,
          action: TypePaymentPassData.PASS_DATA,
          data: {
            cash: 0,
            remaining: 0,
            chanceCash: 0,
            loyalty: amountVal,
            giftCard: 0,
            checkOthers: 0,
            card: 0,
            multiPay: 0,
            loyaltyAvail: detail.customerPoint || 0,
            loyaltyEx: availablePointEx,
            totalValue: detail?.total,
            subTotal: detail.subTotal || 0,
            cardFee: 0,
            saleTax: detail.saleTax || 0,
            usedTax: detail.useTax || 0,
            discount: (detail.discount || 0) + (detail.discountItem || 0),
          } as ICustomerPaymentDetail,
        });
        return detail;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId);
  });

  const onOpen: Ref['open'] = async (billId) => {
    socketContext.switchCusPayment(billId);
    form.resetFields();
    try {
      const detail = await fetchDetail(billId);
      if (detail) {
        setOpen(billId);
      }
    } catch (error) {
    }
  };

  const BalancePoint = () => {
    if (!loyaltySetting) return <>{('0 point = $0')}</>;
    if (balancePoint < 0) {
      return <>0 Point = $0</>;
    }
    return (
      <>
        {balancePoint || 0} point = {formatCurrency(exchangeDollar(+(balancePoint || '')))}
      </>
    );
  };

  const AvailablePoint = () => {
    return (
      <>
        {customerPoint || 0} point = {formatCurrency(exchangeDollar(+(customerPoint || '')))}
      </>
    );
  };

  const FooterContent = () => {
    return <ContainerAmountLoyaltyPoint>
      <div className="amount-item">
        <div className="amount-label">Available:</div>
        <div className="amount-value"><AvailablePoint /></div>
      </div>
      <div className="amount-item">
        <div className="amount-label">Balance:</div>
        <div className="amount-value"><BalancePoint /></div>
      </div>
    </ContainerAmountLoyaltyPoint>;
  };
  const onValuesChange = debounce(() => {
    const detail = form.getFieldValue('detail');
    const amount = form.getFieldValue('amount') || 0;
    socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
      billId,
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.PASS_DATA,
      data: {
        cash: 0,
        remaining: 0,
        chanceCash: 0,
        loyalty: amount,
        giftCard: 0,
        checkOthers: 0,
        card: 0,
        multiPay: 0,

        totalValue: detail?.total,
        subTotal: detail.subTotal || 0,
        cardFee: 0,
        saleTax: detail.saleTax || 0,
        usedTax: detail.useTax || 0,
        discount: (detail.discount || 0) + (detail.discountItem || 0),
      } as ICustomerPaymentDetail,
    });
  }, 1000);

  const handleFinish = async (values: any) => {
    const data = values as { amount: number, detail: IBillInfoResData };
    const payment: ICashierLoyaltyPointPayment = {
      amount: data.amount,
      paymentType: PAYMENT_TYPE.LOYALTY_POINT,
      point: redeemPoint,
    };
    if (payment.amount < data.detail.total) {
      setOpen('');
      cashierBtnMultiPayV3.current?.continueLoyaltyPayWithMultiPay(data.detail.billId, { amount: payment.amount, point: payment.point });
      return;
    }
    setPageLoading(true);
    try {
      const isCompleted = await onPayment(data.detail.billId, payment);
      if (!isCompleted) return;
      setOpen('');
    } catch (error) { } finally {
      setPageLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpen
  }));
  return (
    <Modal
      open={!!billId}
      title='POINTS'
      okText={'Redeem'}
      className='modal-overflow-unset modal-non-opacity'
      onClose={onClose}
      width={'1000px'}
    >
      <Form form={form} onFinish={handleFinish} onValuesChange={onValuesChange}>
        <Form.Item noStyle name={'detail'} />
        <Form.Item noStyle name={'amount'} />
        <Box display='flex' flexDirection='column' gap='2'>
          <Text content_content_2_16_medium color_text_3 text_center style={{ fontSize: 20 }}>
            Enter Amount:
          </Text>
          <Form.Item name={'amount'} noStyle>
            <InputWithKeyboardStyled
              ref={dollarRef}
              inputMode='none'
              autoFocus
              withForm
            />
          </Form.Item>
          <FooterContent />
          <Form.Item name={'amount'} noStyle>
            <DollarAmountKeyboard
              controlRef={dollarRef}
            />
          </Form.Item>
        </Box>
        <ModalActions onClose={onClose} disabledOk={!(_amount > 0 && !(balancePoint < 0))} />
      </Form>
    </Modal>
  );
});
ModalCashierLoyaltyPayment.displayName = 'ModalCashierLoyaltyPayment';
export default ModalCashierLoyaltyPayment;

const ContainerAmountLoyaltyPoint = styled.div`
display: flex;
height: 49px;
justify-content: center;
align-items: center;
gap: 32px;
align-self: stretch;
background: #F5F5F5;
margin: 0 32px;
.amount-item {
  display: flex;
  align-items: center;
  gap: 6px;
  .amount-label {
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  .amount-value {
    color: #1D2129;
    font-family: Poppins;
    font-size: 26px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }
}
`;